import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import type { INetworkDebugRequest } from 'shared/lib/networkDebug'

import { NETWORK_DEBUG_TRANSLATION_KEYS } from './translations'

import styles from './NetworkTiming.module.scss'

const KEYS = NETWORK_DEBUG_TRANSLATION_KEYS

/** Steps recorded under a common name; any other label is a code name and is shown as is. */
const STEP_KEYS: Record<string, string> = {
  fetch: KEYS.timingWaiting,
  waiting: KEYS.timingWaiting,
  download: KEYS.timingDownload,
  request: KEYS.timingRequest,
  store: KEYS.timingStore,
}

const StepLabel = ({ label }: { label: string }) => {
  const key = STEP_KEYS[label]

  return key ? <Translation text={key} span /> : <>{label}</>
}

interface INetworkTimingProps {
  request: INetworkDebugRequest
}

/** The request's steps one after another on one time line, so the slow step stands out. */
export const NetworkTiming = ({ request }: INetworkTimingProps) => {
  const t = useSafetyTranslation()
  const total = request.timings.reduce((sum, timing) => sum + timing.ms, 0) || 1
  const failedIndex = request.state === 'error' ? request.timings.length - 1 : -1
  let start = 0

  return (
    <div className={styles.root}>
      {request.timings.map((timing, index) => {
        const left = (start / total) * 100
        start += timing.ms

        return (
          <div key={`${timing.label}-${index}`} className={styles.row}>
            <span>
              {index === failedIndex ? (
                <Translation
                  text={KEYS.timingFailed}
                  span
                  replaceParams={[
                    {
                      key: '{step}',
                      value: STEP_KEYS[timing.label] ? t(STEP_KEYS[timing.label]) : timing.label,
                    },
                  ]}
                />
              ) : (
                <StepLabel label={timing.label} />
              )}
            </span>
            <span className={styles.track}>
              <i
                className={styles.bar}
                style={{ left: `${left}%`, width: `${Math.max(0.6, (timing.ms / total) * 100)}%` }}
              />
            </span>
            <span className={styles.ms}>{timing.ms.toLocaleString('en-US')} ms</span>
          </div>
        )
      })}
      <div className={styles.total}>
        <Translation
          text={KEYS.timingTotal}
          span
          replaceParams={[{ key: '{ms}', value: Math.round(start).toLocaleString('en-US') }]}
        />
      </div>
    </div>
  )
}

NetworkTiming.displayName = 'NetworkTiming'
